import React, { useEffect, useState } from 'react';

import DeleteRounded from '@mui/icons-material/DeleteRounded';
import {
  Skeleton, Stack, Typography,
} from '@mui/material';
import Switch from '@mui/material/Switch';
import { useNavigate } from 'react-router';

import Button from '../../../../components/Button';
import ContentCard from '../../../../components/ContentCard';
import Modal from '../../../../components/Modal';
import { useDeleteExamMutation, useUpdateExamMutation } from '../../../../services/exams';
import { Exam } from '../../../../services/exams/types';
import useError from '../../../../utils/react/hooks/useError';
import useSuccess from '../../../../utils/react/hooks/useSuccess';
import { SettingsContainer } from '../../../ProfilePage/SettingsTab/index.styles';

import { ActiveExamSettingContainer } from './index.styles';

interface ActionsCardProps {
  exam?: Exam
}

const ActionsCard = ({ exam }: ActionsCardProps) => {
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false);
  const [confirmationModalState, setConfirmationModalState] = useState<{
    title: string;
    description: string;
    buttonLabel: string;
    buttonColor: 'error' | 'success';
  }>({
    title: '',
    description: '',
    buttonLabel: '',
    buttonColor: 'error',
  });

  const [deleteExam, {
    isSuccess: isDeleteExamSuccess,
    isLoading: isDeleteExamLoading,
    isError: isDeleteExamError,
    error: deleteExamError,
  }] = useDeleteExamMutation();

  const [updateExamActiveState, {
    isSuccess: isUpdateExamSuccess,
    isLoading: isUpdateExamLoading,
    isError: isUpdateExamError,
    error: updateExamError,
  }] = useUpdateExamMutation();

  // messages and labels for the comfirmation modal
  useEffect(() => {
    if (exam?.active) {
      setConfirmationModalState({
        title: 'Inactivate Exam',
        description: 'This will prevent the exam from being marked.',
        buttonLabel: 'Inactivate',
        buttonColor: 'error',
      });
    } else {
      setConfirmationModalState({
        title: 'Activate Exam',
        description: 'Once the exam is activated, it can be marked but questions can no longer be added or removed.',
        buttonLabel: 'Activate',
        buttonColor: 'success',
      });
    }
  }, [exam?.active]);

  // Navigate out once the exam is deleted
  useEffect(() => {
    if (isDeleteExamSuccess) {
      setDeleteModalOpen(false);
      navigate('/admin/exams');
    }
  }, [isDeleteExamSuccess]);

  useError(isDeleteExamError, deleteExamError);
  useSuccess(isDeleteExamSuccess, 'Exam deleted');

  useEffect(() => {
    if (isUpdateExamSuccess) {
      setConfirmationModalOpen(false);
    }
  }, [isUpdateExamSuccess]);

  useError(isUpdateExamError, updateExamError);
  useSuccess(isUpdateExamSuccess, 'Exam updated');

  if (!exam) {
    return (
      <Skeleton variant="rectangular" height={128} />
    );
  }

  return (
    <SettingsContainer>
      <ContentCard title="Delete Exam">
        <Stack direction="row" spacing={1}>
          <Button
            color="error"
            size="medium"
            onClick={() => setDeleteModalOpen(true)}
            disabled={!exam?.analytics?.uniqueAttempts && exam.analytics.uniqueAttempts > 0}
            startIcon={(<DeleteRounded />)}
          >
            Delete
          </Button>
        </Stack>
        <Modal
          open={deleteModalOpen}
          handleClose={() => setDeleteModalOpen(false)}
          title="Are you sure?"
          description="This will permanently delete this exam."
          buttons={[
            {
              label: 'Delete',
              onClick: () => deleteExam({ subject: exam.subject, examId: exam.examId }),
              color: 'error',
              loading: isDeleteExamLoading,
            },
          ]}
        />
      </ContentCard>
      <ContentCard title="Actions">
        <ActiveExamSettingContainer>
          <Typography>Inactive</Typography>
          <Switch
            checked={exam.active ?? true}
            onChange={() => setConfirmationModalOpen(true)}
          />
          <Typography>Active</Typography>
        </ActiveExamSettingContainer>
        <Modal
          open={confirmationModalOpen}
          handleClose={() => setConfirmationModalOpen(false)}
          title={confirmationModalState.title}
          description={confirmationModalState.description}
          buttons={[
            {
              label: confirmationModalState.buttonLabel,
              onClick: () => updateExamActiveState({
                subject: exam.subject,
                examId: exam.examId,
                attributes: {
                  active: !exam.active,
                },
              }),
              color: confirmationModalState.buttonColor,
              loading: isUpdateExamLoading,
            },
          ]}
        />
      </ContentCard>
    </SettingsContainer>

  );
};

export default ActionsCard;
